import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Search, Zap, Mail, Phone, Target, Check, Download, Star, Building2, RotateCcw, Reply, HandMetal, PhoneCall, Heart, Sprout, Brain, Sparkles, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import AITemplateAnalytics from './AITemplateAnalytics';

const ICON_MAP = { Zap, Mail, Phone, Target, Building2, RotateCcw, Reply, HandMetal, PhoneCall, Heart, Sprout };
const CATEGORIES = [
  { id: 'all', label: 'All', icon: Sparkles },
  { id: 'cadence', label: 'Cadences', icon: Zap },
  { id: 'email', label: 'Email Templates', icon: Mail },
  { id: 'voice_script', label: 'Voice Scripts', icon: Phone },
  { id: 'agent_preset', label: 'Agent Presets', icon: Brain },
];

const CATEGORY_COLORS = {
  cadence: 'from-violet-500/20 to-violet-600/5 border-violet-500/20',
  email: 'from-blue-500/20 to-blue-600/5 border-blue-500/20',
  voice_script: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/20',
  agent_preset: 'from-amber-500/20 to-amber-600/5 border-amber-500/20',
};

const CATEGORY_BADGE = {
  cadence: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  email: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  voice_script: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  agent_preset: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

function TemplateCard({ template, onInstall, onUninstall, installing }) {
  const Icon = ICON_MAP[template.icon] || Sparkles;
  const gradient = CATEGORY_COLORS[template.category] || 'from-gray-500/20 to-gray-600/5 border-border';
  const badgeColor = CATEGORY_BADGE[template.category] || 'bg-muted text-muted-foreground';

  return (
    <Card
      className={`group relative overflow-hidden border bg-gradient-to-br ${gradient} hover:shadow-lg transition-all duration-300`}
      data-testid={`template-card-${template.id}`}
    >
      {template.is_featured && (
        <div className="absolute top-3 right-3">
          <Badge className="bg-amber-500/90 text-white text-[9px] px-1.5 py-0 border-0">
            <Star className="w-2.5 h-2.5 mr-0.5 fill-current" /> Featured
          </Badge>
        </div>
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-background/80 border border-border/50 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-sm font-semibold leading-tight">{template.name}</CardTitle>
            <Badge variant="secondary" className={`mt-1 text-[9px] px-1.5 py-0 ${badgeColor} border-0`}>
              {template.category === 'voice_script' ? 'Voice' : template.category === 'agent_preset' ? 'Agent' : template.category}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{template.description}</p>

        <div className="flex flex-wrap gap-1">
          {template.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-background/60 text-muted-foreground border border-border/30">
              {tag}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-border/30">
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Download className="w-3 h-3" /> {template.install_count}
            </span>
            {template.rating > 0 && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> {template.rating}
              </span>
            )}
          </div>
          {template.installed ? (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[10px] px-2 border-emerald-500/30 text-emerald-600 dark:text-emerald-400"
              onClick={() => onUninstall(template.id)}
              disabled={installing === template.id}
              data-testid={`uninstall-${template.id}`}
            >
              <Check className="w-3 h-3 mr-1" /> Active
            </Button>
          ) : (
            <Button
              size="sm"
              className="h-7 text-[10px] px-3"
              onClick={() => onInstall(template.id)}
              disabled={installing === template.id}
              data-testid={`install-${template.id}`}
            >
              Install
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AITemplateHub() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [installing, setInstalling] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');

  useEffect(() => { loadTemplates(); }, [search, category]);

  const loadTemplates = async () => {
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (search) params.search = search;
      const qs = new URLSearchParams(params).toString();
      const { data } = await api.get(`/api/ai-templates/templates${qs ? '?' + qs : ''}`);
      setTemplates(data);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async (id) => {
    setInstalling(id);
    try {
      await api.post(`/api/ai-templates/templates/${id}/install`);
      toast.success('Template installed — Brain will use it for future actions');
      loadTemplates();
    } catch {
      toast.error('Failed to install template');
    } finally {
      setInstalling(null);
    }
  };

  const handleUninstall = async (id) => {
    setInstalling(id);
    try {
      await api.post(`/api/ai-templates/templates/${id}/uninstall`);
      toast.success('Template deactivated');
      loadTemplates();
    } catch {
      toast.error('Failed to uninstall template');
    } finally {
      setInstalling(null);
    }
  };

  const installedCount = templates.filter(t => t.installed).length;

  return (
    <div className="space-y-6" data-testid="ai-template-hub">
      <div>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Brain className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">AI Template Hub</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Install templates to supercharge the Brain's autonomous actions</span>
              {installedCount > 0 && (
                <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0">
                  {installedCount} active
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 border-b border-border/50 pb-0" data-testid="template-hub-tabs">
        <button
          onClick={() => setActiveTab('browse')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'browse'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-browse"
        >
          <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Browse
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'performance'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-performance"
        >
          <BarChart3 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
          Performance
        </button>
      </div>

      {activeTab === 'performance' ? (
        <AITemplateAnalytics />
      ) : (
      <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            className="pl-8 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="template-search"
          />
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {CATEGORIES.map(cat => (
            <Button
              key={cat.id}
              variant={category === cat.id ? 'default' : 'ghost'}
              size="sm"
              className={`h-7 text-[11px] px-2.5 ${category === cat.id ? '' : 'text-muted-foreground'}`}
              onClick={() => setCategory(cat.id)}
              data-testid={`filter-${cat.id}`}
            >
              <cat.icon className="w-3 h-3 mr-1" />
              {cat.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="border border-border/50">
              <CardHeader><div className="h-10 w-32 bg-muted animate-pulse rounded" /></CardHeader>
              <CardContent><div className="h-16 bg-muted animate-pulse rounded" /></CardContent>
            </Card>
          ))}
        </div>
      ) : templates.length === 0 ? (
        <Card data-testid="no-templates">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Brain className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground font-medium">No templates found</p>
            <p className="text-xs text-muted-foreground mt-1">Try a different search or category</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="templates-grid">
          {templates.map(t => (
            <TemplateCard
              key={t.id}
              template={t}
              onInstall={handleInstall}
              onUninstall={handleUninstall}
              installing={installing}
            />
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
